/**
 * ThemedTooltip Component
 * 
 * Tooltip component that adapts its appearance based on the active immersive theme.
 * Supports all 5 themes with unique visual characteristics.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useImmersiveTheme } from '../../hooks/useImmersiveTheme';
import { ImmersiveThemeType, ThemedTooltipProps } from '../../types/immersive-theme.types';
import { neumorphismTooltip } from './theme-styles/neumorphism';
import { brutalistTooltip, brutalistTooltipArrow } from './theme-styles/brutalist';
import { retroTerminalTooltip } from './theme-styles/retro-terminal';
import { organicNatureTooltip } from './theme-styles/organic-nature';
import { luxuryGoldTooltip } from './theme-styles/luxury-gold';

// Tooltip container styles per theme
const tooltipStyles: Record<ImmersiveThemeType, React.CSSProperties> = {
  [ImmersiveThemeType.NONE]: {
    background: '#1f2937',
    color: '#ffffff',
    padding: '8px 12px',
    borderRadius: '6px',
    fontSize: '13px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  },
  [ImmersiveThemeType.NEUMORPHISM]: neumorphismTooltip,
  [ImmersiveThemeType.BRUTALIST]: brutalistTooltip,
  [ImmersiveThemeType.RETRO_TERMINAL]: retroTerminalTooltip,
  [ImmersiveThemeType.ORGANIC_NATURE]: organicNatureTooltip,
  [ImmersiveThemeType.LUXURY_GOLD]: luxuryGoldTooltip,
};

// Arrow styles per theme
const getArrowStyles = (theme: ImmersiveThemeType, position: string): React.CSSProperties => {
  const arrowSize = 6;
  const baseArrow: React.CSSProperties = {
    position: 'absolute',
    width: 0,
    height: 0,
    borderStyle: 'solid',
  };

  const arrowColor = theme === ImmersiveThemeType.NONE 
    ? '#1f2937'
    : theme === ImmersiveThemeType.NEUMORPHISM
      ? 'var(--neu-bg)'
      : theme === ImmersiveThemeType.BRUTALIST
        ? 'var(--brut-text)'
        : theme === ImmersiveThemeType.RETRO_TERMINAL
          ? 'var(--term-bg)'
          : theme === ImmersiveThemeType.ORGANIC_NATURE
            ? 'var(--org-primary-dark)'
            : 'var(--lux-bg-elevated)';

  const positionStyles: Record<string, React.CSSProperties> = {
    top: {
      bottom: -arrowSize,
      left: '50%',
      transform: 'translateX(-50%)',
      borderWidth: `${arrowSize}px ${arrowSize}px 0 ${arrowSize}px`,
      borderColor: `${arrowColor} transparent transparent transparent`,
    },
    bottom: {
      top: -arrowSize,
      left: '50%',
      transform: 'translateX(-50%)',
      borderWidth: `0 ${arrowSize}px ${arrowSize}px ${arrowSize}px`,
      borderColor: `transparent transparent ${arrowColor} transparent`,
    },
    left: {
      right: -arrowSize,
      top: '50%',
      transform: 'translateY(-50%)',
      borderWidth: `${arrowSize}px 0 ${arrowSize}px ${arrowSize}px`,
      borderColor: `transparent transparent transparent ${arrowColor}`,
    },
    right: {
      left: -arrowSize,
      top: '50%',
      transform: 'translateY(-50%)',
      borderWidth: `${arrowSize}px ${arrowSize}px ${arrowSize}px 0`,
      borderColor: `transparent ${arrowColor} transparent transparent`,
    },
  };

  return {
    ...baseArrow,
    ...positionStyles[position],
    ...(theme === ImmersiveThemeType.BRUTALIST ? brutalistTooltipArrow : {}),
  };
};

// Position offset configurations
const positionOffsets: Record<string, { x: number; y: number }> = {
  top: { x: 0, y: -8 },
  bottom: { x: 0, y: 8 },
  left: { x: -8, y: 0 },
  right: { x: 8, y: 0 },
};

export const ThemedTooltip: React.FC<ThemedTooltipProps> = ({
  content,
  position = 'top',
  delay = 200,
  disabled = false,
  arrow = true,
  maxWidth = '250px',
  className,
  children,
}) => {
  const { theme, reducedMotion } = useImmersiveTheme();
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Calculate tooltip position
  const calculatePosition = useCallback(() => {
    if (!triggerRef.current || !tooltipRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const offset = positionOffsets[position];

    let top = 0;
    let left = 0;

    switch (position) {
      case 'top':
        top = triggerRect.top - tooltipRect.height + offset.y;
        left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2 + offset.x;
        break;
      case 'bottom':
        top = triggerRect.bottom + offset.y;
        left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2 + offset.x;
        break;
      case 'left':
        top = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2 + offset.y;
        left = triggerRect.left - tooltipRect.width + offset.x;
        break;
      case 'right':
        top = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2 + offset.y;
        left = triggerRect.right + offset.x;
        break;
    }

    // Ensure tooltip stays within viewport
    const padding = 8;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    if (left < padding) left = padding;
    if (left + tooltipRect.width > viewportWidth - padding) {
      left = viewportWidth - tooltipRect.width - padding;
    }
    if (top < padding) top = padding;
    if (top + tooltipRect.height > viewportHeight - padding) {
      top = viewportHeight - tooltipRect.height - padding;
    }

    setTooltipPosition({ top, left });
  }, [position]);

  // Show tooltip
  const showTooltip = useCallback(() => {
    if (disabled) return;
    
    timeoutRef.current = setTimeout(() => {
      setIsVisible(true);
    }, delay);
  }, [disabled, delay]);

  // Hide tooltip
  const hideTooltip = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsVisible(false);
  }, []);

  // Update position when visible
  useEffect(() => {
    if (isVisible) {
      calculatePosition();
      window.addEventListener('scroll', calculatePosition, true);
      window.addEventListener('resize', calculatePosition);
    }

    return () => {
      window.removeEventListener('scroll', calculatePosition, true);
      window.removeEventListener('resize', calculatePosition);
    };
  }, [isVisible, calculatePosition]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Compute tooltip styles
  const computedTooltipStyles: React.CSSProperties = {
    position: 'fixed',
    top: tooltipPosition.top,
    left: tooltipPosition.left,
    zIndex: 9999,
    maxWidth,
    ...tooltipStyles[theme],
    ...(reducedMotion ? { transition: 'none' } : { 
      transition: 'opacity 0.15s ease, transform 0.15s ease',
    }),
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'scale(1)' : 'scale(0.95)',
    pointerEvents: isVisible ? 'auto' : 'none',
  };

  // Render content with theme-specific styling
  const renderContent = () => {
    if (theme === ImmersiveThemeType.RETRO_TERMINAL) {
      return (
        <>
          <span style={{ opacity: 0.6 }}>&gt; </span>
          {content}
        </>
      );
    }
    return content;
  };

  const tooltip = isVisible && (
    <div
      ref={tooltipRef}
      role="tooltip"
      style={computedTooltipStyles}
      className={className}
    >
      {renderContent()}
      {arrow && <span style={getArrowStyles(theme, position)} aria-hidden="true" />}
    </div>
  );

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showTooltip}
        onBlur={hideTooltip}
        style={{ display: 'inline-flex' }}
        aria-describedby={isVisible ? 'tooltip' : undefined}
      >
        {children}
      </span>
      {typeof window !== 'undefined' && createPortal(tooltip, document.body)}
    </>
  );
};

export default ThemedTooltip;
